/**
 * Responsive breakpoint helpers shared by the `Stack` / `Grid` layout
 * primitives: the mobile-first breakpoint order and the
 * flat-value-vs-breakpoint-map type guard. Both consume a
 * `Responsive<T>` value (a flat value OR a breakpoint-keyed partial).
 */
import type { BreakpointKey } from '../tokens/breakpoints';
import type { Responsive } from '../types';

/**
 * Mobile-first breakpoint order. Shared single source of truth — imported
 * by the `Stack` / `Grid` layout primitives so the two never drift
 * (previously each kept its own copy). `xs` is always the base /
 * no-media-query default.
 */
export const BREAKPOINT_ORDER: BreakpointKey[] = ['xs', 'sm', 'md', 'lg', 'xl'];

/**
 * Type-guard: is a `Responsive<T>` value a breakpoint-keyed map (vs a flat
 * value)? An object with at least one breakpoint key (`xs`…`xl`) is treated
 * as a map. Shared by `Stack` and `Grid`.
 */
export function isBreakpointMap<T>(v: Responsive<T>): v is Partial<Record<BreakpointKey, T>> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  return Object.keys(v).some((k) => (BREAKPOINT_ORDER as string[]).includes(k));
}
